import { apiGet, apiPost, type BodyOf, type ResponseOf } from './http';

export type Session = ResponseOf<'/api/v1/auth/me/', 'get'>;
export type Credentials = BodyOf<'/api/v1/auth/login/', 'post'>;
export type AccessGroup = 'Admin' | 'Recruiter' | 'Interviewer';

/**
 * Session-cookie auth (ADR 0003). Nothing here reads or writes storage, and nothing sends a tenant
 * — the backend resolves it from the authenticated user.
 */

/** Django sets the CSRF cookie here; `apiClient` sends it back as `X-CSRFToken` on every write. */
export const fetchCsrfToken = () => apiGet('/api/v1/auth/csrf/');

export const fetchSession = () => apiGet('/api/v1/auth/me/');

export async function signIn(credentials: Credentials): Promise<Session> {
  await fetchCsrfToken();
  return apiPost('/api/v1/auth/login/', credentials);
}

export const signOut = () => apiPost('/api/v1/auth/logout/');

export const groupsOf = (session: Session): AccessGroup[] => session.groups as AccessGroup[];

export const isInGroup = (session: Session, group: AccessGroup): boolean =>
  groupsOf(session).includes(group);

export type Redemption = BodyOf<'/api/v1/auth/invitations/redeem/', 'post'>;

/**
 * Spend a one-time invitation: set a password and **sign in** (backend TASK-100).
 *
 * ⚠️ **Public by necessity and CSRF-protected anyway.** The caller has no session yet — that is what
 * the token is for — so the cookie is fetched first, exactly as `signIn` does: django-ninja gives a
 * route with `auth=None` no CSRF check at all, and this one is `csrf_protected_public` on the server.
 *
 * It answers `Session`, the same payload login returns, because it **signs the person in**: they have
 * just proved possession of a single-use secret *and* chosen the password, which is more than login
 * asks for, and a sign-in form afterwards would only invite a typo.
 *
 * ⚠️ **Every failure is one 422 with one sentence** — unknown, expired, already used, replaced — so a
 * public route cannot be used to ask whether an account exists. The client must not try to be more
 * specific than the server: there is nothing more to know.
 */
export async function redeemInvitation(redemption: Redemption): Promise<Session> {
  await fetchCsrfToken();
  return apiPost('/api/v1/auth/invitations/redeem/', redemption);
}
